/**
 * BlogAnimation — Embeds interactive animation HTML for a blog post.
 *
 * The animation is the visual hero — it previews/introduces the article content.
 * No generic headings like "Interactive animation". The animation's own internal
 * title/badge serves as the visual introduction.
 */

import { useState, useRef, useCallback } from 'react';
import type { BlogInteractiveComponent } from '../../types/blog';
import type { BlogCategory } from '../../types/blog';

interface BlogAnimationProps {
  component?: BlogInteractiveComponent;
  category?: BlogCategory;
  slug: string;
  date: string;
}

// Check which animation files exist at build time (for the null guard)
const animationFiles = import.meta.glob('/content/blog/animations/*.html');

export function BlogAnimation({ component, slug, date }: BlogAnimationProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(520);

  const filename = `${date}-${slug}.html`;
  const animationKey = `/content/blog/animations/${filename}`;

  const handleLoad = useCallback(() => {
    try {
      const doc = iframeRef.current?.contentDocument;
      const wrapper = doc?.querySelector('.animation-wrapper') as HTMLElement;
      if (wrapper) {
        setIframeHeight(wrapper.offsetHeight + 40);
      }
    } catch {
      // cross-origin in production — use default height
    }
  }, []);

  // Only render if the animation file exists
  if (!animationFiles[animationKey]) return null;

  return (
    <section className="max-w-3xl mx-auto my-8">
      {/* No heading — the animation's internal title/badge IS the visual intro */}
      <div className="rounded-2xl overflow-hidden shadow-card border border-gray-100">
        <iframe
          ref={iframeRef}
          src={`/animations/${filename}`}
          title={component?.title || 'Animation'}
          className="w-full border-0 block"
          style={{ height: `${iframeHeight}px` }}
          onLoad={handleLoad}
        />
      </div>
    </section>
  );
}
